/**
 * Route exporters — uniform `{name, points}` route → GPX/Google
 * Maps URL plus a `download` helper.
 *
 * Exports are pure, deterministic and do NOT touch the network. The
 * tool page calls these directly and triggers a browser download.
 *
 * (KML export removed 2026-05-22 — formato deprecado.)
 */
import { stopsOf, localizedText } from "./editorPoints";

const xmlEscape = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

/** GPX 1.1 export, with optional named `<wpt>` waypoints in the
 *  document head. Two modes:
 *
 *  - `points: Array<{lat,lng,ele?,time?}>` → single `<trk>` with one
 *    `<trkseg>` containing all points (legacy, kept for callers that
 *    don't care about stage structure).
 *  - `tracks: Array<{name, points, waypoints?}>` → **one `<trk>` per
 *    track**, each with its own `<name>` and `<trkseg>`. When a track
 *    carries its own `waypoints`, each `<wpt>` is emitted at document
 *    level with a `<type>motri-etapa:{name}</type>` marker so that
 *    parsers (`parseGPX` below) can re-attribute each wpt to its
 *    originating etapa on round-trip — solving the "circular junction"
 *    bug where a wpt shared between etapa N and N+1 (or between the
 *    final etapa and the first etapa on a loop trip) was attributed
 *    to BOTH etapas via spatial proximity, duplicating it.
 *
 *  Backwards-compatible: `<type>` is a standard GPX 1.1 element, so
 *  third-party viewers ignore the `motri-etapa:` prefix and just show
 *  the wpts as normal markers.
 *
 *  Legacy callers passing a flat `waypoints` array continue to work
 *  unchanged (no per-etapa marker is emitted). Added in b99.10.43,
 *  rewritten in b99.10.56 (2026-05-21).
 *
 *  GPX spec mandates `<wpt>` before `<trk>` in document order. */
export function toGPX({ name, points, tracks, waypoints = [], pois = [], mode = "exact" }) {
  const trackName = xmlEscape(name || "MotoTrips Route");
  // EDITOR-EXPORT-PROFILE (2026-06-19) — perfis de exportação GPX:
  //  • "exact" (default): `<trk>` denso (traçado exato, p/ Garmin / Detecht
  //    Follow-Route-Line / GPS) + `<rte>` com as paragens.
  //  • "curvy": OMITE o `<trk>` denso e emite um `<rte>` com pontos de
  //    afinação amostrados (~1/8 km, Douglas–Peucker) + paragens, para apps
  //    que RECALCULAM o trajeto (Kurviger, Calimoto) o reconstruírem fiel às
  //    curvas, sem placar centenas de waypoints automáticos a partir do track.
  const curvy = mode === "curvy";

  const serialiseWpt = (w, etapaName) => {
    if (!Number.isFinite(w?.lat) || !Number.isFinite(w?.lng)) return null;
    const ele = w.ele != null ? `    <ele>${w.ele}</ele>\n` : "";
    const nameTag = w.name ? `    <name>${xmlEscape(w.name)}</name>\n` : "";
    const desc = w.desc ? `    <desc>${xmlEscape(w.desc)}</desc>\n` : "";
    const typeTag = etapaName
      ? `    <type>motri-etapa:${xmlEscape(etapaName)}</type>\n`
      : "";
    return `  <wpt lat="${w.lat}" lon="${w.lng}">\n${ele}${nameTag}${desc}${typeTag}  </wpt>`;
  };

  const serialiseTrkpt = (p) => {
    const ele = p.ele != null ? `      <ele>${p.ele}</ele>\n` : "";
    const time = p.time ? `      <time>${xmlEscape(p.time)}</time>\n` : "";
    return `    <trkpt lat="${p.lat}" lon="${p.lng}">\n${ele}${time}    </trkpt>`;
  };

  // EDITOR-ADD-POI (2026-06-10) — paragens manuais como `<wpt>` MARCADO
  // com `<type>motri-poi:CATEGORY</type>` (+ `<sym>`). O marcador faz o
  // re-import devolvê-las como `pois` (marcadores), NUNCA como stops de
  // routing — evitando que a última paragem vire o Fim da rota.
  const serialisePoiWpt = (w) => {
    if (!Number.isFinite(w?.lat) || !Number.isFinite(w?.lng)) return null;
    const nameTag = w.name ? `    <name>${xmlEscape(w.name)}</name>\n` : "";
    const cat = xmlEscape(w.category || "other");
    const symTag = `    <sym>${cat}</sym>\n`;
    const typeTag = `    <type>motri-poi:${cat}</type>\n`;
    return `  <wpt lat="${w.lat}" lon="${w.lng}">\n${nameTag}${symTag}${typeTag}  </wpt>`;
  };

  // Multi-track mode preserves the etapa structure across roundtrip.
  // Single-track mode kept for backward compat with simple callers.
  const trkList = (Array.isArray(tracks) && tracks.length > 0)
    ? tracks
    : [{ name: trackName, points: points || [] }];

  // Per-track wpts (with `motri-etapa` marker) take precedence over
  // the flat `waypoints` arg when the tracks carry their own wpts.
  const tracksHaveWpts = Array.isArray(tracks)
    && tracks.some((t) => Array.isArray(t?.waypoints) && t.waypoints.length > 0);

  let wptsBlock = "";
  if (tracksHaveWpts) {
    const lines = [];
    for (const t of tracks) {
      if (!Array.isArray(t?.waypoints)) continue;
      const etapa = t.name || trackName;
      for (const w of t.waypoints) {
        if (!w?.name) continue; // skip anonymous
        const line = serialiseWpt(w, etapa);
        if (line) lines.push(line);
      }
    }
    wptsBlock = lines.join("\n");
  } else {
    wptsBlock = (waypoints || [])
      .map((w) => serialiseWpt(w, null))
      .filter(Boolean)
      .join("\n");
  }

  // Anexa as paragens manuais (de tracks[].pois ou do `pois` plano),
  // depois dos waypoints de routing.
  const poiLines = [];
  if (Array.isArray(tracks)) {
    for (const t of tracks) {
      for (const p of (Array.isArray(t?.pois) ? t.pois : [])) {
        const line = serialisePoiWpt(p);
        if (line) poiLines.push(line);
      }
    }
  }
  for (const p of (Array.isArray(pois) ? pois : [])) {
    const line = serialisePoiWpt(p);
    if (line) poiLines.push(line);
  }
  if (poiLines.length) {
    wptsBlock = wptsBlock ? `${wptsBlock}\n${poiLines.join("\n")}` : poiLines.join("\n");
  }

  // EDITOR-ROUTE-OPTIONS-GPX (2026-06-17) — persiste as opções de rota
  // (evitar autoestradas/portagens) por etapa num `<extensions>` dentro
  // do `<trk>`. Só emite quando CONHECIDAS (booleanos) — tracks de origem
  // desconhecida (GPX em bruto, `null`) não emitem nada. Viewers de
  // terceiros ignoram extensões desconhecidas; o nosso parser relê-as no
  // round-trip para repor o estado "evita / permite" da etapa.
  const serialiseTrkExtensions = (t) => {
    const ro = t?.routeOptions;
    if (!ro) return "";
    const ah = ro.avoidHighways;
    const at = ro.avoidTolls;
    if (typeof ah !== "boolean" && typeof at !== "boolean") return "";
    const attrs = [];
    if (typeof ah === "boolean") attrs.push(`avoidHighways="${ah}"`);
    if (typeof at === "boolean") attrs.push(`avoidTolls="${at}"`);
    return `    <extensions>\n      <motriRouteOptions ${attrs.join(" ")}/>\n    </extensions>\n`;
  };

  const trks = curvy ? "" : trkList
    .filter((t) => Array.isArray(t?.points) && t.points.length > 0)
    .map((t) => {
      const tn = xmlEscape(t.name || trackName);
      const ext = serialiseTrkExtensions(t);
      const pts = t.points.map(serialiseTrkpt).join("\n");
      return `  <trk>
    <name>${tn}</name>
${ext}    <trkseg>
${pts}
    </trkseg>
  </trk>`;
    })
    .join("\n");

  // EDITOR-POI-AS-VIA (2026-06-18) — emite um `<rte>` por etapa que tenha
  // PDIs, com `<rtept>` na ordem do trajeto (origem → waypoints de routing
  // + PDIs intercalados por posição ao longo da geometria → destino).
  // Apps de navegação que usam ROTAS (ex.: Garmin) passam explicitamente
  // pelos PDIs ("o utilizador quer parar ali"). O `<trk>` (traçado exato)
  // e os `<wpt motri-poi>` (marcadores com nome+categoria) mantêm-se. No
  // reimport, o `parseGPX` ignora features `_gpxType==='rte'` (togeojson)
  // → o round-trip do MotoTrips não duplica etapas. Só emitimos `<rte>`
  // quando a etapa tem PDIs (mudança cirúrgica/aditiva).
  const nearestIdx = (pts, item) => {
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < pts.length; i++) {
      const dLat = pts[i].lat - item.lat;
      const dLng = pts[i].lng - item.lng;
      const d = dLat * dLat + dLng * dLng;
      if (d < bestD) { bestD = d; best = i; }
    }
    return best;
  };
  const serialiseRtept = (p) => {
    const nameTag = p.name ? `      <name>${xmlEscape(p.name)}</name>\n` : "";
    return `    <rtept lat="${p.lat}" lon="${p.lng}">\n${nameTag}    </rtept>`;
  };
  // Fase C (2026-06-19) — emite `<rte>` para TODAS as etapas com ≥2 pontos:
  // a sequência são as PARAGENS (origem + waypoints nomeados + PDIs +
  // destino), NUNCA pontos de estrada (esses só no `<trk>`). Garante
  // `stop`→`<wpt>`+`<rtept>`. (Antes só emitia quando havia PDIs.)
  const buildCurvyRte = (t) => {
    const pts = t.points;
    if (!Array.isArray(pts) || pts.length < 2) return null;
    const tn2 = xmlEscape(t.name || trackName);
    const cWpts = (Array.isArray(t.waypoints) ? t.waypoints : [])
      .filter((w) => Number.isFinite(w?.lat) && Number.isFinite(w?.lng) && w?.name);
    const cPois = (Array.isArray(t.pois) ? t.pois : [])
      .filter((p) => Number.isFinite(p?.lat) && Number.isFinite(p?.lng));
    const cStops = [...cWpts, ...cPois].map((it) => ({
      lat: it.lat, lng: it.lng, name: it.name || null, _i: nearestIdx(pts, it),
    }));
    // Orçamento de pontos de afinação ≈ 1 / 8 km (~12/100 km), limitado a 120
    // e nunca menos que as paragens + extremos.
    let km = 0;
    for (let i = 1; i < pts.length; i++) km += haversineKm(pts[i - 1], pts[i]);
    const budget = Math.max(
      Math.min(pts.length, cStops.length + 2),
      Math.min(120, Math.round(km / 8) + 2),
    );
    const shaping = smartSampleForGmaps(pts.map((p) => ({ lat: p.lat, lng: p.lng })), budget)
      .map((sp) => ({ lat: sp.lat, lng: sp.lng, name: null, _i: nearestIdx(pts, sp) }));
    const merged = [...shaping, ...cStops].sort((a, b) => a._i - b._i);
    const dd = [];
    for (const p of merged) {
      const last = dd[dd.length - 1];
      if (last && Math.abs(last.lat - p.lat) < 1e-7 && Math.abs(last.lng - p.lng) < 1e-7) {
        if (!last.name && p.name) last.name = p.name;
        continue;
      }
      dd.push({ ...p });
    }
    return `  <rte>\n    <name>${tn2}</name>\n${dd.map(serialiseRtept).join("\n")}\n  </rte>`;
  };
  const rtes = trkList
    .filter((t) => Array.isArray(t?.points) && t.points.length >= 2)
    .map((t) => {
      if (curvy) return buildCurvyRte(t);
      const pts = t.points;
      const wpts = (Array.isArray(t.waypoints) ? t.waypoints : [])
        .filter((w) => Number.isFinite(w?.lat) && Number.isFinite(w?.lng) && w?.name);
      const pois = (Array.isArray(t.pois) ? t.pois : [])
        .filter((p) => Number.isFinite(p?.lat) && Number.isFinite(p?.lng));
      const vias = [...wpts, ...pois]
        .map((it) => ({ lat: it.lat, lng: it.lng, name: it.name || null, _i: nearestIdx(pts, it) }))
        .sort((a, b) => a._i - b._i);
      const seq = [
        { lat: pts[0].lat, lng: pts[0].lng, name: null },
        ...vias.map((v) => ({ lat: v.lat, lng: v.lng, name: v.name })),
        { lat: pts[pts.length - 1].lat, lng: pts[pts.length - 1].lng, name: null },
      ];
      // Dedup de coords consecutivas iguais (origem/destino podem
      // coincidir com um via); preserva o nome se existir.
      const dedup = [];
      for (const p of seq) {
        const last = dedup[dedup.length - 1];
        if (last && Math.abs(last.lat - p.lat) < 1e-7 && Math.abs(last.lng - p.lng) < 1e-7) {
          if (!last.name && p.name) last.name = p.name;
          continue;
        }
        dedup.push({ ...p });
      }
      const tn = xmlEscape(t.name || trackName);
      return `  <rte>\n    <name>${tn}</name>\n${dedup.map(serialiseRtept).join("\n")}\n  </rte>`;
    })
    .filter(Boolean)
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="MotoTrips" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata><name>${trackName}</name></metadata>
${wptsBlock}${wptsBlock ? "\n" : ""}${rtes}${rtes ? "\n" : ""}${trks}
</gpx>`;
}

/** KML export removed 2026-05-22. */

/** Attach per-point elevation from a segment's elevation samples, by
 *  proportional index mapping. Returns points unchanged when the segment
 *  has no altitude data. Shared by Editor + public route GPX export. */
function attachEleFromSeg(points, seg) {
  const coords = seg?.elevation?.has_altitude ? (seg.elevation.coordinates || []) : [];
  if (coords.length < 2 || points.length < 2) return points;
  const K = coords.length;
  return points.map((p, i) => {
    const j = Math.round((i / (points.length - 1)) * (K - 1));
    const alt = coords[j]?.alt;
    return alt != null ? { ...p, ele: alt } : p;
  });
}

/**
 * EDITOR-EXPORT-CENTRALISE (2026-06-19) — build the rich `tracks[]`
 * payload for `toGPX` from editor/route segments. ONE track per stage,
 * with: dense geometry (`routedCoords` preferred, else `points`) +
 * elevation, named start/end `<wpt>`, manual stops as POIs, and per-stage
 * `routeOptions`. Shared by the Editor AND the public route page so both
 * produce identical GPX (same etapas, names, options, profile support).
 */
export function buildGpxTracksFromSegments(segments) {
  if (!Array.isArray(segments)) return [];
  return segments
    .map((s, i) => {
      const pts = (Array.isArray(s.routedCoords) && s.routedCoords.length > 1)
        ? s.routedCoords.map(([lat, lng]) => ({ lat, lng }))
        : (Array.isArray(s.points)
            ? s.points.map((p) => ({ lat: p.lat, lng: p.lng, ele: p.ele, time: p.time }))
            : []);
      const stops = stopsOf(s)
        .filter((p) => localizedText(p.name) && Number.isFinite(p.lat) && Number.isFinite(p.lng));
      const waypoints = stops
        .filter((p) => p.role === "start" || p.role === "end")
        .map((p) => ({ lat: p.lat, lng: p.lng, name: localizedText(p.name) }));
      const pois = stops
        .filter((p) => p.role !== "start" && p.role !== "end")
        .map((p) => ({ lat: p.lat, lng: p.lng, name: localizedText(p.name), category: p.category || "other" }));
      return {
        name: s.name || `Etapa ${i + 1}`,
        points: attachEleFromSeg(pts, s),
        waypoints,
        pois,
        routeOptions: s.routeOptions || null,
      };
    })
    .filter((t) => t.points.length > 0);
}

/**
 * Google Maps directions URL.
 *
 * Hard limit: Google Maps only lets the user load up to 10 waypoints
 * via a `dir/` URL (1 origin + up to 8 stops + 1 destination). When
 * the route has more, we evenly subsample to 10 points and return
 * `{ url, truncated: true, originalCount, exportedCount }`. The UI
 * surfaces this so the user knows GPX is the fidelity-preserving
 * option.
 */
export const GOOGLE_MAPS_URL_MAX_WAYPOINTS = 10;

/**
 * Perpendicular distance of `p` to segment `a–b` (planar, with a
 * cos(lat) correction so longitude isn't over-weighted). Units are
 * arbitrary — used only to RANK points by significance, never as a
 * real distance.
 */
function _perpDist(p, a, b) {
  const kx = Math.cos((((a.lat + b.lat) / 2) * Math.PI) / 180) || 1;
  const ax = a.lng * kx, ay = a.lat;
  const bx = b.lng * kx, by = b.lat;
  const px = p.lng * kx, py = p.lat;
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/**
 * Smart waypoint sampling for Google Maps URLs (b99.11.x).
 *
 * Instead of evenly subsampling (which on a long road drops the very
 * corners that make Google follow the intended route), we keep the
 * `maxPoints` MOST SIGNIFICANT points using a top-N Douglas–Peucker:
 * start/end are always kept, then we repeatedly add the point of
 * greatest perpendicular deviation from the current simplified line.
 * This minimises the discrepancy between the original track and the
 * route Google reconstructs between the exported waypoints.
 */
export function smartSampleForGmaps(points, maxPoints = GOOGLE_MAPS_URL_MAX_WAYPOINTS, seedIndices = []) {
  const n = points.length;
  if (n <= maxPoints) return points.slice();
  if (maxPoints < 2) return [points[0], points[n - 1]];
  const keep = new Set([0, n - 1]);
  // EDITOR-POI-AS-VIA (2026-06-18) — força a inclusão dos índices semente
  // (PDIs) para que o Google encaminhe por eles; nunca são descartados na
  // amostragem. Ignora 0/n-1 (já garantidos) e índices fora de gama.
  for (const idx of seedIndices) {
    if (Number.isInteger(idx) && idx > 0 && idx < n - 1) keep.add(idx);
  }
  // Se as sementes já enchem (ou excedem) o limite, mantemos início/fim +
  // tantas sementes quantas couberem (sem poda por desvio).
  if (keep.size >= maxPoints) {
    return [...keep].sort((a, b) => a - b).slice(0, maxPoints).map((i) => points[i]);
  }
  const segs = [];
  const evalSeg = (s, e) => {
    if (e <= s + 1) return;
    let maxD = -1, idx = -1;
    for (let i = s + 1; i < e; i++) {
      const d = _perpDist(points[i], points[s], points[e]);
      if (d > maxD) { maxD = d; idx = i; }
    }
    if (idx >= 0) segs.push({ s, e, idx, dev: maxD });
  };
  // Semeia a avaliação em cada intervalo entre índices já mantidos
  // (início/fim + PDIs), para a poda por desvio operar dentro de cada gap.
  const sortedKeep = [...keep].sort((a, b) => a - b);
  for (let k = 0; k < sortedKeep.length - 1; k++) evalSeg(sortedKeep[k], sortedKeep[k + 1]);
  while (keep.size < maxPoints && segs.length) {
    let bi = 0;
    for (let i = 1; i < segs.length; i++) if (segs[i].dev > segs[bi].dev) bi = i;
    const seg = segs.splice(bi, 1)[0];
    if (keep.has(seg.idx)) continue;
    keep.add(seg.idx);
    evalSeg(seg.s, seg.idx);
    evalSeg(seg.idx, seg.e);
  }
  return [...keep].sort((a, b) => a - b).map((i) => points[i]);
}

/**
 * EDITOR-POI-AS-VIA — map each PDI (`mustInclude`) to the nearest point
 * of the geometry and return the indices to force-keep during sampling.
 * Threshold (~111m) drops PDIs that belong to other sub-stages.
 */
function _gmapsSeedIndices(points, mustInclude) {
  const seedIndices = [];
  if (Array.isArray(mustInclude) && mustInclude.length) {
    const THRESH = 1e-6; // graus² ≈ (1e-3°)² ≈ 111m
    for (const m of mustInclude) {
      if (!Number.isFinite(m?.lat) || !Number.isFinite(m?.lng)) continue;
      let best = -1, bestD = Infinity;
      for (let i = 0; i < points.length; i++) {
        const dLat = points[i].lat - m.lat;
        const dLng = points[i].lng - m.lng;
        const d = dLat * dLat + dLng * dLng;
        if (d < bestD) { bestD = d; best = i; }
      }
      if (best >= 0 && bestD <= THRESH) seedIndices.push(best);
    }
  }
  return seedIndices;
}

/**
 * Count how many mandatory stops (seed indices) survived into the
 * exported (sampled) point set. Used to warn the user when a stage has
 * more stops than fit in the URL's point budget. Matches by exact
 * lat/lng (exported points are the same objects as `points`).
 */
function _countKeptStops(points, seedIndices, exported) {
  const seedSet = [...new Set(seedIndices)];
  const total = seedSet.length;
  if (exported.length >= points.length) return { total, kept: total };
  const keptKeys = new Set(exported.map((p) => `${p.lat},${p.lng}`));
  const kept = seedSet.filter((idx) => keptKeys.has(`${points[idx].lat},${points[idx].lng}`)).length;
  return { total, kept };
}

export function toGoogleMapsURL({ points, mustInclude = [], routeOptions = null }) {
  if (!points.length) {
    return { url: null, truncated: false, originalCount: 0, exportedCount: 0 };
  }
  let exported = points;
  let truncated = false;
  const seedIndices = _gmapsSeedIndices(points, mustInclude);
  if (points.length > GOOGLE_MAPS_URL_MAX_WAYPOINTS) {
    truncated = true;
    exported = smartSampleForGmaps(points, GOOGLE_MAPS_URL_MAX_WAYPOINTS, seedIndices);
  }
  const path = exported
    .map((p) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`)
    .join("/");
  // EDITOR-GMAPS-EMBED-OPTIONS (2026-07-03) — when the stage explicitly
  // AVOIDS highways/tolls, embed that in the URL so Google honours it on
  // open. The documented Maps URLs API (`?api=1&…&avoid=…`) is the only
  // format that carries `avoid`; the plain `/dir/{path}` format cannot.
  // Only switches format when there's a real avoid=true (null/false keep
  // the well-tested path format → zero regression for unknown/allowed).
  const avoid = [];
  if (routeOptions?.avoidHighways === true) avoid.push("highways");
  if (routeOptions?.avoidTolls === true) avoid.push("tolls");
  let url = `https://www.google.com/maps/dir/${path}`;
  if (avoid.length) {
    const fmt = (p) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;
    const origin = exported[0];
    const dest = exported[exported.length - 1];
    const vias = exported.slice(1, -1);
    const params = new URLSearchParams();
    params.set("api", "1");
    params.set("origin", fmt(origin));
    params.set("destination", fmt(dest));
    if (vias.length) params.set("waypoints", vias.map(fmt).join("|"));
    params.set("travelmode", "driving");
    params.set("avoid", avoid.join(","));
    url = `https://www.google.com/maps/dir/?${params.toString()}`;
  }
  return {
    url,
    truncated,
    originalCount: points.length,
    exportedCount: exported.length,
    avoidHighways: routeOptions?.avoidHighways === true,
    avoidTolls: routeOptions?.avoidTolls === true,
    ...(() => { const s = _countKeptStops(points, seedIndices, exported); return { stopsTotal: s.total, stopsKept: s.kept, stopsDropped: s.total - s.kept }; })(),
  };
}

/**
 * EDITOR-GMAPS-25PTS (2026-07-06) — OPTIONAL "extended" variant.
 *
 * The plain `/maps/dir/A/B/…/` PATH format carries far more waypoints
 * than the documented `?api=1` format (which the current export uses).
 * In practice Google renders up to ~25 stops this way (same trick as
 * "MoreThan10"). This function is ADDITIVE — the current export path is
 * untouched; this only feeds the optional 2nd column in the dialog.
 */
export const GOOGLE_MAPS_URL_MAX_WAYPOINTS_EXT = 25;

/**
 * Build the `data=` protocol-buffer string that carries travel mode
 * (driving) + best-effort avoid flags. Encoding is UNDOCUMENTED and may
 * change without notice. Structure (see analysis): field4 › field4
 * holds field2=avoid { 1:highways, 2:tolls } and field3=travel mode.
 * The `m<N>` count is the number of DESCENDANT elements in the subtree.
 *  - driving only            → !4m2!4m1!3e0
 *  - + avoid highways        → !4m4!4m3!2m1!1b1!3e0
 *  - + avoid highways+tolls  → !4m5!4m4!2m2!1b1!2b1!3e0
 */
export function buildGmapsDataParam({ avoidHighways = false, avoidTolls = false } = {}) {
  const MODE = "!3e0"; // driving
  const avoidTokens = [];
  if (avoidHighways) avoidTokens.push("!1b1");
  if (avoidTolls) avoidTokens.push("!2b1");
  if (!avoidTokens.length) return "!4m2!4m1!3e0";
  const avoidMsg = `!2m${avoidTokens.length}${avoidTokens.join("")}`;
  const avoidCount = 1 + avoidTokens.length;   // !2m.. + tokens
  const innerDesc = avoidCount + 1;            // + travel mode
  const inner = `!4m${innerDesc}${avoidMsg}${MODE}`;
  const innerCount = 1 + innerDesc;            // !4m.. + descendants
  return `!4m${innerCount}${inner}`;
}

export function toGoogleMapsURLExtended({ points, mustInclude = [], routeOptions = null }) {
  if (!points.length) {
    return { url: null, truncated: false, originalCount: 0, exportedCount: 0, avoidHighways: false, avoidTolls: false };
  }
  const seedIndices = _gmapsSeedIndices(points, mustInclude);
  let exported = points;
  let truncated = false;
  if (points.length > GOOGLE_MAPS_URL_MAX_WAYPOINTS_EXT) {
    truncated = true;
    exported = smartSampleForGmaps(points, GOOGLE_MAPS_URL_MAX_WAYPOINTS_EXT, seedIndices);
  }
  const path = exported
    .map((p) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`)
    .join("/");
  const avoidHighways = routeOptions?.avoidHighways === true;
  const avoidTolls = routeOptions?.avoidTolls === true;
  const data = buildGmapsDataParam({ avoidHighways, avoidTolls });
  return {
    url: `https://www.google.com/maps/dir/${path}/data=${data}`,
    truncated,
    originalCount: points.length,
    exportedCount: exported.length,
    avoidHighways,
    avoidTolls,
    ...(() => { const s = _countKeptStops(points, seedIndices, exported); return { stopsTotal: s.total, stopsKept: s.kept, stopsDropped: s.total - s.kept }; })(),
  };
}

/** Extract a flat [{lat,lng}] list from a segment, preferring the dense
 *  routed geometry (`routedCoords` = [[lat,lng]…]) and falling back to
 *  the segment's `points`. Used by the unified Google Maps export. */
function _stagePoints(seg) {
  if (Array.isArray(seg?.routedCoords) && seg.routedCoords.length >= 2) {
    return seg.routedCoords
      .map(([lat, lng]) => ({ lat, lng }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  }
  return (seg?.points || [])
    .filter((p) => Number.isFinite(p?.lat) && Number.isFinite(p?.lng))
    .map((p) => ({ lat: p.lat, lng: p.lng }));
}

/**
 * Unified Google Maps export — one (or more) URL(s) per stage, built
 * from the stage's dense routed geometry with smart sampling. Used by
 * both the Editor and the public route page so the behaviour is
 * identical.
 *
 * `partsPerStage` (default 1) splits each stage into N contiguous,
 * roughly-equal sub-parts FOR EXPORT ONLY (via `splitSegmentByN`, a
 * pure function — the caller's segments are never mutated). More parts
 * → shorter sections → denser waypoints per URL → Google follows the
 * original road more closely. Stages with too little resolution to
 * split fall back to a single URL.
 *
 * Stages with <2 usable points are skipped.
 */
export function buildGmapsStageExports(segments, { partsPerStage = 1 } = {}) {
  const out = [];
  (segments || []).forEach((seg, index) => {
    const basePts = _stagePoints(seg);
    if (basePts.length < 2) return;

    // Decide whether this stage can be split into the requested parts.
    let subSegs = [{ seg, partLabel: null }];
    const n = Math.max(1, Math.floor(partsPerStage));
    if (n > 1 && basePts.length > n) {
      try {
        const chunks = splitSegmentByN(seg, n);
        subSegs = chunks.map((c, ci) => ({ seg: c, partLabel: { i: ci + 1, n } }));
      } catch {
        subSegs = [{ seg, partLabel: null }];
      }
    }

    subSegs.forEach(({ seg: sub, partLabel }, subIdx) => {
      const pts = _stagePoints(sub);
      if (pts.length < 2) return;
      // EDITOR-POI-AS-VIA — os PDIs da etapa são waypoints obrigatórios
      // na URL do Google (não descartados na amostragem de 10). Em etapas
      // divididas, o limiar dentro de `toGoogleMapsURL` mantém só os PDIs
      // desta sub-etapa.
      // EDITOR-POINT-MODEL-UNIFY (Fase A4) — paragens vêm de `points[].kind`
      // ("stop", excl. extremos). Fallback ao legacy `seg.pois` para fontes
      // ainda não migradas (páginas públicas).
      const kindStops = stopsOf(seg)
        .filter((p) => p.role !== "start" && p.role !== "end"
          && Number.isFinite(p?.lat) && Number.isFinite(p?.lng));
      const segPois = kindStops.length
        ? kindStops
        : (Array.isArray(seg.pois) ? seg.pois : [])
            .filter((p) => Number.isFinite(p?.lat) && Number.isFinite(p?.lng));
      const cur = toGoogleMapsURL({ points: pts, mustInclude: segPois, routeOptions: seg.routeOptions || null });
      if (!cur.url) return;
      const ext = toGoogleMapsURLExtended({ points: pts, mustInclude: segPois, routeOptions: seg.routeOptions || null });
      const baseName = seg.name || `Etapa ${index + 1}`;
      out.push({
        id: `${seg.id || `stage_${index}`}${partLabel ? `_p${subIdx + 1}` : ""}`,
        index,
        part: partLabel,
        name: partLabel ? `${baseName} (${partLabel.i}/${partLabel.n})` : baseName,
        distanceKm: Number(sub.distanceKm) || 0,
        durationMin: Number(sub.durationMin) || 0,
        totalCount: pts.length,
        keptCount: cur.exportedCount,
        color: seg.color || "#f97316",
        avoidHighways: seg.routeOptions?.avoidHighways === true,
        avoidTolls: seg.routeOptions?.avoidTolls === true,
        url: cur.url,
        stopsTotal: cur.stopsTotal,
        stopsDropped: cur.stopsDropped,
        url25: ext.url,
        keptCount25: ext.exportedCount,
        stopsDropped25: ext.stopsDropped,
      });
    });
  });
  return out;
}

/** Browser-side download trigger. */
export function downloadFile(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Chrome has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Chunk a waypoint list into groups of up to `maxPerChunk` (default 10
 * — the Google Maps URL hard limit). Each chunk overlaps with the next
 * by 1 point so that the user can drive parts back-to-back: the last
 * waypoint of part N is the first waypoint of part N+1.
 *
 *   chunkWaypointsForGMaps([a,b,c,d,e,f,g,h,i,j,k,l,m], 10)
 *     → [[a,b,c,d,e,f,g,h,i,j], [j,k,l,m]]
 *
 * When `points.length <= maxPerChunk` returns a single chunk.
 */
export function chunkWaypointsForGMaps(points, maxPerChunk = 10) {
  if (!Array.isArray(points) || points.length === 0) return [];
  if (points.length <= maxPerChunk) return [points.slice()];
  const chunks = [];
  let i = 0;
  while (i < points.length) {
    const end = Math.min(i + maxPerChunk, points.length);
    chunks.push(points.slice(i, end));
    if (end >= points.length) break;
    i = end - 1; // overlap 1 point with the next chunk
  }
  return chunks;
}

/**
 * Split a route point list into N approximately equal chunks by
 * cumulative distance (Haversine). Used by "Exportar GPXs por partes".
 * Returns `Array<Array<Point>>` with each subarray sharing a 1-point
 * overlap with the next so a downstream merge would be lossless.
 */
export function splitRouteByDistance(points, nParts) {
  if (!Array.isArray(points) || points.length < 2 || nParts < 2) {
    return [points.slice()];
  }
  const total = totalDistanceKm(points);
  if (total <= 0) return [points.slice()];
  const cutKm = total / nParts;

  const result = [];
  let current = [points[0]];
  let acc = 0;
  let nextCutAt = cutKm;

  for (let i = 1; i < points.length; i++) {
    const seg = totalDistanceKm([points[i - 1], points[i]]);
    acc += seg;
    current.push(points[i]);
    if (acc >= nextCutAt && result.length < nParts - 1) {
      result.push(current);
      current = [points[i]]; // overlap 1 point
      nextCutAt += cutKm;
    }
  }
  result.push(current);
  return result;
}

/**
 * Split a single segment into N consecutive sub-segments of
 * approximately equal cumulative distance.
 *
 * Reuses `splitRouteByDistance` on the segment's best available
 * geometry (`routedCoords` when present, else `points`). Each
 * resulting sub-segment carries:
 *   - `points` / `routedCoords`: the chunk's coords (same data — we
 *     don't try to "un-route" the polyline)
 *   - `waypoints`: subset of the original's named waypoints whose
 *     positions fall in this chunk's geographic window (nearest-
 *     point lookup against the chunk's polyline)
 *   - `distanceKm`: recomputed from the chunk (NOT divided pro-rata —
 *     real cumulative distance per chunk)
 *   - `durationMin`: pro-rated by distance share (null if the input
 *     has no duration)
 *   - `name`: `${original.name} ({i}/{n})`
 *   - `color`: inherited (visual continuity)
 *   - `source` / `routingSource`: inherited
 *
 * Caller is responsible for assigning new ids and replacing the
 * original segment in the segments[] array.
 */
export function splitSegmentByN(seg, n) {
  if (!seg || typeof seg !== "object") throw new Error("splitSegmentByN: invalid segment");
  if (!Number.isInteger(n) || n < 2) throw new Error("splitSegmentByN: n must be an integer >= 2");

  // Prefer the snapped polyline because it is dense and accurate;
  // fall back to user points when the segment was never routed.
  const sourceArr = Array.isArray(seg.routedCoords) && seg.routedCoords.length >= 2
    ? seg.routedCoords.map(([lat, lng]) => ({ lat, lng }))
    : (Array.isArray(seg.points) ? seg.points : []);
  if (sourceArr.length < 2) {
    throw new Error("splitSegmentByN: segment has no usable geometry");
  }
  if (n > sourceArr.length - 1) {
    throw new Error("splitSegmentByN: n exceeds segment resolution");
  }

  const chunks = splitRouteByDistance(sourceArr, n);
  const hasRoutedSource = Array.isArray(seg.routedCoords) && seg.routedCoords.length >= 2;
  const totalKm = totalDistanceKm(sourceArr);

  // Pre-compute each waypoint's nearest chunk index so a wpt sitting
  // exactly on a junction doesn't get duplicated.
  const wpts = Array.isArray(seg.waypoints) ? seg.waypoints : [];
  const chunkOfWpt = new Map(); // wpt index -> chunk index
  for (let wi = 0; wi < wpts.length; wi++) {
    const wp = wpts[wi];
    if (!Number.isFinite(wp?.lat) || !Number.isFinite(wp?.lng)) continue;
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let ci = 0; ci < chunks.length; ci++) {
      const chunk = chunks[ci];
      const step = Math.max(1, Math.floor(chunk.length / 100));
      for (let pi = 0; pi < chunk.length; pi += step) {
        const dlat = chunk[pi].lat - wp.lat;
        const dlng = chunk[pi].lng - wp.lng;
        const d2 = dlat * dlat + dlng * dlng;
        if (d2 < bestDist) { bestDist = d2; bestIdx = ci; }
      }
    }
    if (bestIdx >= 0) chunkOfWpt.set(wi, bestIdx);
  }

  return chunks.map((chunk, idx) => {
    const chunkKm = totalDistanceKm(chunk);
    const durationMin = (typeof seg.durationMin === "number" && totalKm > 0)
      ? Math.round(seg.durationMin * (chunkKm / totalKm))
      : null;
    const myWpts = [];
    for (let wi = 0; wi < wpts.length; wi++) {
      if (chunkOfWpt.get(wi) === idx) myWpts.push(wpts[wi]);
    }
    return {
      source: seg.source,
      name: `${seg.name || "Etapa"} (${idx + 1}/${n})`,
      startName: idx === 0 ? (seg.startName || null) : null,
      endName: idx === n - 1 ? (seg.endName || null) : null,
      points: chunk.map((p) => ({ lat: p.lat, lng: p.lng })),
      waypoints: myWpts,
      routedCoords: hasRoutedSource ? chunk.map((p) => [p.lat, p.lng]) : null,
      bridges: [],
      distanceKm: chunkKm,
      durationMin,
      color: seg.color,
      routingSource: seg.routingSource || null,
      isRouting: false,
    };
  });
}

/**
 * Build a cumulative distance array (km) for an `{lat,lng}` point list.
 * `cumDistKm[i]` is the cumulative distance from `points[0]` up to
 * `points[i]` (so `cumDistKm[0] === 0` and the last element is the
 * total distance). Used by cut-mode helpers below — kept exported so
 * the UI can lookup distance at a given polyline index and vice-versa.
 */
export function cumulativeDistancesKm(points) {
  const out = [];
  if (!Array.isArray(points) || points.length === 0) return out;
  out.push(0);
  for (let i = 1; i < points.length; i++) {
    out.push(out[i - 1] + haversineKm(points[i - 1], points[i]));
  }
  return out;
}

/**
 * Find the polyline index closest to a target cumulative distance (km).
 * Returns the index `i` such that `cumDistKm[i]` is the nearest to
 * `targetKm`. Binary search → O(log n).
 */
export function indexAtDistanceKm(cumDistKm, targetKm) {
  if (!Array.isArray(cumDistKm) || cumDistKm.length === 0) return 0;
  if (targetKm <= 0) return 0;
  const last = cumDistKm.length - 1;
  if (targetKm >= cumDistKm[last]) return last;
  let lo = 0;
  let hi = last;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (cumDistKm[mid] < targetKm) lo = mid + 1;
    else hi = mid;
  }
  // pick the closer of lo and lo-1
  if (lo > 0 && Math.abs(cumDistKm[lo - 1] - targetKm) < Math.abs(cumDistKm[lo] - targetKm)) {
    return lo - 1;
  }
  return lo;
}

/**
 * Split a single segment at an array of cut distances expressed in km
 * from the segment start. Returns `cutDistancesKm.length + 1`
 * consecutive sub-segments. Used by the Editor's "Modo de corte"
 * (interactive split UI).
 *
 *   splitSegmentAtCuts(seg, [120, 250]) →
 *     [sub_0_to_120, sub_120_to_250, sub_250_to_end]
 *
 * - `cutDistancesKm` is sanitised: removes NaN, sorts ascending,
 *   dedupes, drops entries ≤ 0 or ≥ total km, and drops near-duplicates
 *   (< 0.5 km apart).
 * - Each sub-segment uses the indices from `cumulativeDistancesKm` so
 *   the resulting polylines are contiguous and lossless (last point of
 *   sub N === first point of sub N+1).
 * - `durationMin` is pro-rated by distance share.
 * - `waypoints` are re-attributed by nearest sub-polyline.
 * - `routedCoords` are kept only when the source had them.
 * - `color` is inherited; the EditorPage is expected to override it
 *   per sub-segment using its palette (cores não em uso no mapa).
 * - `startName` / `endName` are kept only at the very first / very
 *   last sub-segments — the interior junctions get `null` so the
 *   caller can rename them based on user input.
 */
export function splitSegmentAtCuts(seg, cutDistancesKm) {
  if (!seg || typeof seg !== "object") throw new Error("splitSegmentAtCuts: invalid segment");
  if (!Array.isArray(cutDistancesKm)) throw new Error("splitSegmentAtCuts: cuts must be an array");

  const sourceArr = Array.isArray(seg.routedCoords) && seg.routedCoords.length >= 2
    ? seg.routedCoords.map(([lat, lng]) => ({ lat, lng }))
    : (Array.isArray(seg.points) ? seg.points : []);
  if (sourceArr.length < 2) throw new Error("splitSegmentAtCuts: segment has no usable geometry");

  const cum = cumulativeDistancesKm(sourceArr);
  const totalKm = cum[cum.length - 1] || 0;
  if (totalKm <= 0) throw new Error("splitSegmentAtCuts: segment has zero length");

  // Sanitise cuts: keep only finite, strictly inside (0, total),
  // ascending and without near-duplicates (0.5 km tolerance).
  const cuts = [...cutDistancesKm]
    .filter((d) => Number.isFinite(d) && d > 0.5 && d < totalKm - 0.5)
    .sort((a, b) => a - b)
    .filter((d, i, arr) => i === 0 || d - arr[i - 1] > 0.5);
  if (cuts.length === 0) throw new Error("splitSegmentAtCuts: no valid cut positions");

  // Map each cut to its polyline index.
  const cutIdxs = cuts.map((d) => indexAtDistanceKm(cum, d));
  // Boundaries: [0, cutIdx0, cutIdx1, ..., last].
  const bounds = [0, ...cutIdxs, sourceArr.length - 1];

  const hasRoutedSource = Array.isArray(seg.routedCoords) && seg.routedCoords.length >= 2;
  const subCount = bounds.length - 1;

  // Build chunks with overlap-by-1 so reassembly is lossless.
  const chunks = [];
  for (let i = 0; i < subCount; i++) {
    const from = bounds[i];
    const to = bounds[i + 1];
    if (to <= from) continue;
    chunks.push(sourceArr.slice(from, to + 1));
  }

  // Re-attribute named waypoints to nearest sub-segment.
  const wpts = Array.isArray(seg.waypoints) ? seg.waypoints : [];
  const chunkOfWpt = new Map();
  for (let wi = 0; wi < wpts.length; wi++) {
    const wp = wpts[wi];
    if (!Number.isFinite(wp?.lat) || !Number.isFinite(wp?.lng)) continue;
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let ci = 0; ci < chunks.length; ci++) {
      const chunk = chunks[ci];
      const step = Math.max(1, Math.floor(chunk.length / 100));
      for (let pi = 0; pi < chunk.length; pi += step) {
        const dlat = chunk[pi].lat - wp.lat;
        const dlng = chunk[pi].lng - wp.lng;
        const d2 = dlat * dlat + dlng * dlng;
        if (d2 < bestDist) { bestDist = d2; bestIdx = ci; }
      }
    }
    if (bestIdx >= 0) chunkOfWpt.set(wi, bestIdx);
  }

  return chunks.map((chunk, idx) => {
    const chunkKm = totalDistanceKm(chunk);
    const durationMin = (typeof seg.durationMin === "number" && totalKm > 0)
      ? Math.round(seg.durationMin * (chunkKm / totalKm))
      : null;
    const myWpts = [];
    for (let wi = 0; wi < wpts.length; wi++) {
      if (chunkOfWpt.get(wi) === idx) myWpts.push(wpts[wi]);
    }
    return {
      source: seg.source,
      name: `${seg.name || "Etapa"} (${idx + 1}/${chunks.length})`,
      startName: idx === 0 ? (seg.startName || null) : null,
      endName: idx === chunks.length - 1 ? (seg.endName || null) : null,
      points: chunk.map((p) => ({ lat: p.lat, lng: p.lng })),
      waypoints: myWpts,
      routedCoords: hasRoutedSource ? chunk.map((p) => [p.lat, p.lng]) : null,
      bridges: [],
      distanceKm: chunkKm,
      durationMin,
      color: seg.color,
      routingSource: seg.routingSource || null,
      isRouting: false,
    };
  });
}

/**
 * From a palette and a list of currently-in-use colors, return the
 * subset of palette colors NOT in use, preserving palette order. If
 * the palette is fully exhausted, returns the full palette as a
 * fallback (so callers always get at least one color). Used by the
 * cut-mode UI so newly-created sub-segments get visually distinct
 * colors that don't collide with sibling stages on the map.
 */
export function pickUnusedColors(palette, inUseColors) {
  const inUse = new Set((inUseColors || []).filter(Boolean));
  const free = palette.filter((c) => !inUse.has(c));
  return free.length > 0 ? free : palette.slice();
}

/** Concatenate the `points` of every segment in order, optionally using
 *  the routed (road-following) coords when available. Returns a single
 *  point list ready to feed into `toGPX` / etc. */
export function concatSegments(segments, { preferRouted = true } = {}) {
  if (!segments?.length) return [];
  const out = [];
  for (const seg of segments) {
    const src = preferRouted && seg.routedCoords?.length >= 2
      ? seg.routedCoords.map(([lat, lng]) => ({ lat, lng }))
      : seg.points;
    for (const p of src) out.push(p);
  }
  return out;
}

/** Concatenate the user-chosen waypoints (NEVER the routed coords) so
 *  Google-Maps-URL exports preserve the user's stops. */
export function concatWaypoints(segments) {
  return concatSegments(segments, { preferRouted: false });
}

/** Concatenate every segment's NAMED waypoints (the human-readable
 *  POI/stop list — `{lat, lng, name}`) in segment order. Used by GPX
 *  export so the exported file keeps a copy of every named stop,
 *  not just the geometry. Anonymous/coord-only entries are skipped
 *  because GPX wpt without a name has no value over an existing
 *  trkpt. */
export function concatNamedWaypoints(segments) {
  if (!segments?.length) return [];
  const out = [];
  for (const seg of segments) {
    if (!Array.isArray(seg.waypoints)) continue;
    for (const wp of seg.waypoints) {
      if (!Number.isFinite(wp?.lat) || !Number.isFinite(wp?.lng)) continue;
      if (!wp.name) continue; // skip anonymous
      out.push({ lat: wp.lat, lng: wp.lng, name: wp.name });
    }
  }
  return out;
}

/**
 * Merge two or more segments into a single new segment.
 *
 * The resulting segment is built so that the visual representation on
 * the map and the downstream exporters keep working without any
 * special-case handling:
 *
 *   - `points`: concat of every segment's `points` (user stops), with
 *     junction dedup (last point of N == first point of N+1 within
 *     ~50m → keep only one copy). This preserves the human-meaningful
 *     waypoints used by Google Maps URL exports.
 *   - `routedCoords`: concat of every segment's snapped polyline when
 *     ALL inputs have one (same dedup logic). If any input has no
 *     `routedCoords`, the merged result drops them — `routingSource`
 *     becomes "merged" so the caller can decide whether to re-route.
 *   - `waypoints`: concat of every segment's named waypoints (no
 *     dedup — the user may have the same POI on adjacent days and
 *     the editor renders them as separate pins).
 *   - `distanceKm`: sum of inputs.
 *   - `durationMin`: sum if every input has a numeric duration; else
 *     null.
 *   - `name` / `startName` / `endName`: first segment's `startName`
 *     and last segment's `endName` (falls back to truncated name
 *     concat when those aren't set).
 *   - `color`: inherits from the FIRST segment so the user keeps
 *     visual continuity.
 *   - `source` / `routingSource`: same value when all inputs agree;
 *     else "merged".
 *   - `bridges` / `isRouting`: reset.
 *
 *  The caller is responsible for: (a) ensuring `segs` is in canonical
 *  order (adjacent in the segments[] array) and (b) generating a new
 *  `id` for the returned segment.
 *
 *  Dedup tolerances (b99.10.65): points/routedCoords use ~100m,
 *  waypoints use ~250m. The previous 50m tolerance let geocoder
 *  micro-jitter on the same locality survive as twin pins on the
 *  map at every stage junction.
 */
export function mergeSegments(segs) {
  if (!Array.isArray(segs) || segs.length < 2) {
    throw new Error("mergeSegments requires at least 2 segments");
  }
  // Tolerâncias separadas por tipo de dado.
  //
  //  - POINTS_EPS / ROUTED_EPS = ~100m. Os `points` são stops do
  //    utilizador (e.g. "Porto" no fim da etapa N e início da N+1).
  //    O geocoder do Google às vezes devolve coords ligeiramente
  //    diferentes para o mesmo local entre chamadas, e a polyline
  //    snapped pode terminar ~50-80m antes do stop e a seguinte
  //    começar ~50-80m depois. 50m apertava demais e duplicava
  //    pontos de contacto reais.
  //  - WPT_EPS = ~250m. Os waypoints nomeados (e.g. "Aveiro" como
  //    pin no mapa) são tipicamente o MESMO local repetido entre
  //    etapas, mas com tolerância mais generosa porque o utilizador
  //    pode ter usado URLs Google Maps diferentes que apontam para
  //    coordenadas distintas do "centro" da localidade.
  const POINTS_EPS = 0.001;  // ~100m
  const ROUTED_EPS = 0.001;  // ~100m
  const WPT_EPS    = 0.0025; // ~250m
  const near = (a, b, eps) =>
    a && b &&
    Math.abs(a.lat - b.lat) < eps &&
    Math.abs(a.lng - b.lng) < eps;
  const same = (a, b) => near(a, b, POINTS_EPS);

  // ─── points (user stops) ───────────────────────────────────────
  const points = [];
  for (let i = 0; i < segs.length; i++) {
    const ps = segs[i].points || [];
    if (i > 0 && ps.length > 0 && points.length > 0) {
      const prevLast = points[points.length - 1];
      if (same(prevLast, ps[0])) {
        for (let j = 1; j < ps.length; j++) points.push(ps[j]);
        continue;
      }
    }
    for (const p of ps) points.push(p);
  }

  // ─── routedCoords (snapped polyline) ───────────────────────────
  const allRouted = segs.every((s) => Array.isArray(s.routedCoords) && s.routedCoords.length >= 2);
  let routedCoords = null;
  if (allRouted) {
    routedCoords = [];
    for (let i = 0; i < segs.length; i++) {
      const rc = segs[i].routedCoords;
      if (i > 0 && rc.length > 0 && routedCoords.length > 0) {
        const [prevLat, prevLng] = routedCoords[routedCoords.length - 1];
        const [curLat, curLng] = rc[0];
        if (Math.abs(prevLat - curLat) < ROUTED_EPS && Math.abs(prevLng - curLng) < ROUTED_EPS) {
          for (let j = 1; j < rc.length; j++) routedCoords.push(rc[j]);
          continue;
        }
      }
      for (const c of rc) routedCoords.push(c);
    }
  }

  // ─── waypoints (named POIs) — dedup at junctions ───────────────
  // Sem este passo o resultado tinha "Porto" duas vezes em pins
  // sobrepostos no mapa: último wpt da etapa N + primeiro wpt da
  // N+1 = mesmo local. Compara apenas o último adicionado com o
  // primeiro do segmento seguinte (não faz dedup global porque o
  // utilizador pode legitimamente visitar a mesma cidade duas
  // vezes ao longo do passeio).
  const waypoints = [];
  for (let i = 0; i < segs.length; i++) {
    const wpts = Array.isArray(segs[i].waypoints) ? segs[i].waypoints : [];
    for (let j = 0; j < wpts.length; j++) {
      const wp = wpts[j];
      if (j === 0 && i > 0 && waypoints.length > 0) {
        const prev = waypoints[waypoints.length - 1];
        if (near(prev, wp, WPT_EPS)) {
          // Mantém o nome mais informativo (o que existir) — se o
          // segundo tem nome e o anterior não, faz upgrade.
          if (!prev.name && wp.name) prev.name = wp.name;
          continue;
        }
      }
      waypoints.push(wp);
    }
  }

  // ─── meta ──────────────────────────────────────────────────────
  const distanceKm = segs.reduce((acc, s) => acc + (Number(s.distanceKm) || 0), 0);
  const allHaveDuration = segs.every((s) => typeof s.durationMin === "number" && Number.isFinite(s.durationMin));
  const durationMin = allHaveDuration ? segs.reduce((acc, s) => acc + s.durationMin, 0) : null;

  const first = segs[0];
  const last = segs[segs.length - 1];
  const startName = first.startName || null;
  const endName = last.endName || null;
  const name = (startName && endName)
    ? `${startName} / ${endName}`
    : (first.name && last.name && first.name !== last.name)
      ? `${first.name} + ${last.name}`
      : (first.name || "Etapa unida");

  const sources = new Set(segs.map((s) => s.source));
  const source = sources.size === 1 ? [...sources][0] : "merged";
  const routingSources = new Set(segs.map((s) => s.routingSource).filter(Boolean));
  const routingSource = routingSources.size === 1 ? [...routingSources][0] : (routingSources.size > 1 ? "merged" : null);

  return {
    source,
    name,
    startName,
    endName,
    points,
    waypoints,
    routedCoords,
    bridges: [],
    distanceKm,
    durationMin,
    color: first.color,
    routingSource,
    isRouting: false,
  };
}

/** Slugify a route name into a safe file basename. */
export function safeBasename(name, fallback = "rota") {
  const base = (name || fallback)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return base || fallback;
}

/** Total distance (km) of an `{lat,lng}` point list. Haversine. */
export function totalDistanceKm(points) {
  if (!points || points.length < 2) return 0;
  const R = 6371;
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const lat1 = (a.lat * Math.PI) / 180;
    const lat2 = (b.lat * Math.PI) / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    total += 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  }
  return total;
}

/**
 * Return `{ start, end }` for a segment using the snapped polyline
 * when present, falling back to the user points. Used for gap
 * detection between adjacent stages and for the dashed-line overlay
 * that signals discontinuities on the map.
 *
 * Returns `null` when the segment has no usable geometry.
 */
export function endpointsOf(seg) {
  if (!seg || typeof seg !== "object") return null;
  if (Array.isArray(seg.routedCoords) && seg.routedCoords.length >= 2) {
    const [sLat, sLng] = seg.routedCoords[0];
    const [eLat, eLng] = seg.routedCoords[seg.routedCoords.length - 1];
    return { start: { lat: sLat, lng: sLng }, end: { lat: eLat, lng: eLng } };
  }
  if (Array.isArray(seg.points) && seg.points.length >= 1) {
    return {
      start: seg.points[0],
      end: seg.points[seg.points.length - 1],
    };
  }
  return null;
}

/**
 * Haversine distance in km between two `{lat,lng}` points. Exported
 * separately because it's used by gap detection and other quick
 * proximity checks outside of full-route distance computations.
 */
export function haversineKm(a, b) {
  if (!a || !b || !Number.isFinite(a.lat) || !Number.isFinite(b.lat)) return Infinity;
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

/**
 * Detect "gaps" between adjacent segments — pairs where the end of
 * segment N is more than `toleranceKm` away from the start of segment
 * N+1. Returned in segment order; empty array means the route is
 * fully connected.
 *
 * Default tolerance is 1 km (b99.10.66 — discutido com utilizador):
 * generoso o suficiente para acomodar variantes de geocoding de
 * uma mesma localidade ("Aveiro centro" vs "Aveiro estação"), mas
 * apertado o suficiente para apanhar gaps reais.
 *
 * Each gap entry is:
 *   {
 *     fromIndex: number,     // index of segment ending the gap
 *     toIndex: number,       // index of segment starting after gap
 *     fromName: string|null, // human label of the ending side
 *     toName: string|null,   // human label of the starting side
 *     end: {lat,lng},        // geographic coords for the dashed line
 *     start: {lat,lng},
 *     distanceKm: number,    // haversine across the gap
 *   }
 */
export function detectGaps(segments, toleranceKm = 1) {
  if (!Array.isArray(segments) || segments.length < 2) return [];
  const gaps = [];
  for (let i = 0; i < segments.length - 1; i++) {
    const a = endpointsOf(segments[i]);
    const b = endpointsOf(segments[i + 1]);
    if (!a || !b) continue;
    const d = haversineKm(a.end, b.start);
    if (d > toleranceKm) {
      gaps.push({
        fromIndex: i,
        toIndex: i + 1,
        fromName: segments[i].endName || segments[i].name || null,
        toName: segments[i + 1].startName || segments[i + 1].name || null,
        end: a.end,
        start: b.start,
        distanceKm: d,
      });
    }
  }
  return gaps;
}

